import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';

const PROJECT_STATUS_META = {
  not_started: { label: 'Not Started', color: 'default' },
  in_progress: { label: 'In Progress', color: 'primary' },
  on_hold: { label: 'On Hold', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  delayed: { label: 'Delayed', color: 'error' },
};

const PROJECT_STATUS_ALIASES = {
  'Not Started': 'not_started',
  'In Progress': 'in_progress',
  'On Hold': 'on_hold',
  'On Track': 'on_hold',
  Completed: 'completed',
  Delayed: 'delayed',
};

const normalizeProjectStatus = (status) => {
  if (!status || typeof status !== 'string') {
    return '';
  }

  if (PROJECT_STATUS_META[status]) {
    return status;
  }

  if (PROJECT_STATUS_ALIASES[status]) {
    return PROJECT_STATUS_ALIASES[status];
  }

  const lowerStatus = status.toLowerCase();
  if (PROJECT_STATUS_META[lowerStatus]) {
    return lowerStatus;
  }

  return status;
};

export const getProjectStatusLabel = (status) => {
  const normalizedStatus = normalizeProjectStatus(status);
  return PROJECT_STATUS_META[normalizedStatus]?.label || status;
};

export const getProjectStatusColor = (status) => {
  const normalizedStatus = normalizeProjectStatus(status);
  return PROJECT_STATUS_META[normalizedStatus]?.color || 'default';
};

export const formatDate = (dateString) => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

export const formatDateTime = (dateString) => {
  if (!dateString) {
    return '-';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const dateStr = formatDate(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${dateStr} ${hours}:${minutes}`;
};

export const formatDateMMDDYYYY = formatDate;

// Activity related helper methods shared across dashboard and activity pages.
export const getActivityVisualConfig = (action, entityType, metadata = null) => {
  const nextStatus = metadata?.changes?.status?.new;
  if (action === 'completed' || nextStatus === 'completed') {
    return { icon: CheckCircleIcon, color: '#2e7d32', type: 'completed' };
  }
  if (action === 'created') {
    return { icon: AddCircleIcon, color: '#1976d2', type: 'created' };
  }
  if (action === 'deleted') {
    return { icon: WarningIcon, color: '#d32f2f', type: 'warning' };
  }
  if (action === 'updated' || action === 'status_changed') {
    return { icon: EditIcon, color: '#9c27b0', type: 'updated' };
  }
  if (entityType === 'project') {
    return { icon: WarningIcon, color: '#ed6c02', type: 'warning' };
  }
  return { icon: CheckCircleIcon, color: '#2e7d32', type: 'completed' };
};

export const formatRelativeTime = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};